import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { assignLocation } from "../lib/browserNavigation";
import { createDocumentServiceClient, type DocumentFile, type DocumentFolder } from "./client";

type DocumentListStatus = "idle" | "loading" | "ready" | "error";
export type DocumentFolderFilter = string;
export type PrepareShareResult = { outcome: "ready"; file: File } | { outcome: "fallback" | "error"; message?: string };
export type CommitShareResult = { outcome: "shared" | "cancelled" } | { outcome: "blocked"; message: string };
export type ShareProgress = {
  lengthComputable: boolean;
  loaded: number;
  total: number | null;
  percentage: number | null;
};

function initialDocumentFolderFilter(): DocumentFolderFilter {
  if (typeof window === "undefined") {
    return "root";
  }

  const params = new URLSearchParams(window.location.search);
  const folderId = params.get("folder")?.trim();
  return folderId || "root";
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
}

// Reads the presigned S3 GET response incrementally so real download progress is available
// whenever the response reports Content-Length, without changing the request itself: it is
// still a bare fetch() with no headers, so no MedoPilot Authorization header is attached.
// When a readable stream body isn't available (or the length isn't known), this degrades to a
// single blob() read with indeterminate progress rather than inventing a fake percentage.
async function readBlobWithProgress(response: Response, onProgress?: (progress: ShareProgress) => void): Promise<Blob> {
  const contentLength = Number.parseInt(response.headers.get("content-length") ?? "", 10);
  const lengthComputable = Number.isFinite(contentLength) && contentLength > 0;
  const reader = response.body?.getReader();

  if (!reader) {
    return response.blob();
  }

  const chunks: BlobPart[] = [];
  let loaded = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (!value) {
      continue;
    }

    chunks.push(value);
    loaded += value.byteLength;
    onProgress?.({
      lengthComputable,
      loaded,
      total: lengthComputable ? contentLength : null,
      percentage: lengthComputable ? Math.round((loaded / contentLength) * 100) : null,
    });
  }

  return new Blob(chunks);
}

function openEmailFallback(filename: string) {
  const subject = encodeURIComponent(`Sharing ${filename} from MedoPilot`);
  const body = encodeURIComponent(
    `MedoPilot has downloaded "${filename}" to your device. Please attach the downloaded file to this email before sending.`,
  );
  assignLocation(`mailto:?subject=${subject}&body=${body}`);
}

export function useDocuments() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.documentService?.apiBaseUrl;
  const [documents, setDocuments] = useState<DocumentFile[]>([]);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [trashedDocuments, setTrashedDocuments] = useState<DocumentFile[]>([]);
  const [folderFilter, setFolderFilter] = useState<DocumentFolderFilter>(initialDocumentFolderFilter);
  const [status, setStatus] = useState<DocumentListStatus>("idle");
  const [folderStatus, setFolderStatus] = useState<DocumentListStatus>("idle");
  const [trashStatus, setTrashStatus] = useState<DocumentListStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [trashError, setTrashError] = useState<string | null>(null);
  const [shareProgress, setShareProgress] = useState<ShareProgress | null>(null);
  const downloadingIdsRef = useRef(new Set<string>());
  const preparingShareIdsRef = useRef(new Set<string>());
  const emptyingTrashRef = useRef(false);
  const documentsRequestIdRef = useRef(0);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createDocumentServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const refreshFolders = useCallback(async () => {
    if (!client) {
      setFolders([]);
      setFolderStatus("error");
      setFolderError("Folders are not available yet.");
      return;
    }

    setFolderStatus("loading");
    setFolderError(null);
    try {
      const nextFolders = await client.listFolders();
      setFolders(nextFolders);
      setFolderStatus("ready");
    } catch (nextError) {
      setFolderStatus("error");
      setFolderError(nextError instanceof Error ? nextError.message : "MedoPilot could not load your folders.");
    }
  }, [client]);

  const refreshDocuments = useCallback(async () => {
    if (!client) {
      setDocuments([]);
      setStatus("error");
      setError("My Files are not available yet.");
      return;
    }

    setStatus("loading");
    setError(null);
    const requestId = documentsRequestIdRef.current + 1;
    documentsRequestIdRef.current = requestId;
    try {
      const nextDocuments = await client.listDocuments(folderFilter === "all" ? "root" : folderFilter);
      if (documentsRequestIdRef.current !== requestId) {
        return;
      }
      setDocuments(nextDocuments);
      setStatus("ready");
    } catch (nextError) {
      if (documentsRequestIdRef.current !== requestId) {
        return;
      }
      setStatus("error");
      setError(nextError instanceof Error ? nextError.message : "MedoPilot could not load your files.");
    }
  }, [client, folderFilter]);

  const findDocumentById = useCallback(
    async (documentId: string): Promise<DocumentFile | null> => {
      if (!client) {
        return null;
      }

      try {
        const allDocuments = await client.listDocuments();
        return allDocuments.find((item) => item.document_id === documentId) ?? null;
      } catch {
        return null;
      }
    },
    [client],
  );

  const refreshTrash = useCallback(async () => {
    if (!client) {
      setTrashedDocuments([]);
      setTrashStatus("error");
      setTrashError("Trash is not available yet.");
      return;
    }

    setTrashStatus("loading");
    setTrashError(null);
    try {
      const nextDocuments = await client.listTrashedDocuments();
      setTrashedDocuments(nextDocuments);
      setTrashStatus("ready");
    } catch (nextError) {
      setTrashStatus("error");
      setTrashError(nextError instanceof Error ? nextError.message : "MedoPilot could not load Trash.");
    }
  }, [client]);

  const openDocument = useCallback(
    async (documentId: string, disposition: "attachment" | "inline" = "inline") => {
      if (!client) {
        setError("My Files are not available yet.");
        return;
      }

      const targetWindow = window.open("about:blank", "_blank");
      if (!targetWindow) {
        setError("We could not open your file. Please allow pop-ups for MedoPilot and try again.");
        return;
      }

      targetWindow.opener = null;

      try {
        setError(null);
        const result = await client.createDownloadUrl(documentId, disposition);
        targetWindow.location.href = result.download_url;
      } catch (nextError) {
        targetWindow.close();
        setError(nextError instanceof Error ? nextError.message : "MedoPilot could not prepare your file download.");
      }
    },
    [client],
  );

  const getDocumentUrl = useCallback(
    async (
      documentId: string,
      disposition: "attachment" | "inline" = "inline",
      variant: "original" | "preview" | "thumbnail" = "original",
    ) => {
      if (!client) {
        setError("My Files are not available yet.");
        return null;
      }

      try {
        setError(null);
        return await client.createDownloadUrl(documentId, disposition, variant);
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "MedoPilot could not prepare your file download.");
        return null;
      }
    },
    [client],
  );

  const fetchOriginalBlob = useCallback(
    async (documentId: string, onProgress?: (progress: ShareProgress) => void) => {
      if (!client) {
        throw new Error("My Files are not available yet.");
      }

      const result = await client.createDownloadUrl(documentId, "attachment", "original");
      const response = await fetch(result.download_url);
      if (!response.ok) {
        throw new Error("The file request failed.");
      }

      return readBlobWithProgress(response, onProgress);
    },
    [client],
  );

  const downloadDocument = useCallback(
    async (documentId: string, filename = "medopilot-document", onProgress?: (progress: ShareProgress) => void) => {
      if (!client) {
        setError("My Files are not available yet.");
        return false;
      }
      if (downloadingIdsRef.current.has(documentId)) {
        return false;
      }

      downloadingIdsRef.current.add(documentId);
      try {
        setError(null);
        const blob = await fetchOriginalBlob(documentId, onProgress);
        triggerBlobDownload(blob, filename);
        return true;
      } catch {
        setError("MedoPilot could not download this file.");
        return false;
      } finally {
        downloadingIdsRef.current.delete(documentId);
      }
    },
    [client, fetchOriginalBlob],
  );

  // Two-step sharing. Web Share requires transient user activation, which browsers can
  // consider expired after even a brief awaited network call. So all network/security work
  // (presigned URL + S3 fetch) happens here, ahead of time, producing a ready-to-use File.
  // The actual navigator.share() call is issued separately (see commitShare) directly from
  // the click that confirms sharing, with no awaited work in between.
  //
  // Share feedback messages are returned to the caller rather than pushed through the shared
  // `error` state: that state also gates whether the document list itself renders, and a
  // per-item share notice must never hide the rest of My Files (Download must stay reachable).
  const prepareShare = useCallback(
    async (documentId: string, filename: string, contentType: string): Promise<PrepareShareResult> => {
      if (!client) {
        setError("My Files are not available yet.");
        return { outcome: "error", message: "My Files are not available yet." };
      }
      if (preparingShareIdsRef.current.has(documentId)) {
        return { outcome: "error" };
      }

      const nativeShareAvailable = typeof navigator !== "undefined" && typeof navigator.share === "function";

      if (!nativeShareAvailable) {
        // No native share support at all (typical of most desktop browsers today): fall back
        // straight to the existing secure download plus a prefilled email, since there is no
        // share sheet to prepare a file for.
        preparingShareIdsRef.current.add(documentId);
        setShareProgress({ lengthComputable: false, loaded: 0, total: null, percentage: null });
        try {
          const downloaded = await downloadDocument(documentId, filename, setShareProgress);
          if (!downloaded) {
            return { outcome: "error", message: "MedoPilot could not prepare this file right now. Please try again." };
          }
          openEmailFallback(filename);
          return {
            outcome: "fallback",
            message:
              "Native sharing isn't available in this browser. We started a download and opened your email app — attach the downloaded file before sending.",
          };
        } finally {
          preparingShareIdsRef.current.delete(documentId);
          setShareProgress(null);
        }
      }

      // A same-tick, network-free feasibility check: navigator.canShare() does not require
      // user activation and does not need real file bytes, only the name/type metadata we
      // already have. This lets us skip fetching the private file entirely when the device
      // could never share it.
      const placeholderFile = new File([], filename, { type: contentType });
      const canShareThisFile = typeof navigator.canShare !== "function" || navigator.canShare({ files: [placeholderFile] });

      if (!canShareThisFile) {
        // A device that otherwise offers navigator.share but rejects this file: keep this
        // soft, since Download remains available from the same file actions as always.
        return {
          outcome: "fallback",
          message: "This file type can't be shared directly from this device. You can still download it from the file actions.",
        };
      }

      preparingShareIdsRef.current.add(documentId);
      setShareProgress({ lengthComputable: false, loaded: 0, total: null, percentage: null });
      try {
        const blob = await fetchOriginalBlob(documentId, setShareProgress);
        const file = new File([blob], filename, { type: contentType || blob.type });
        return { outcome: "ready", file };
      } catch {
        return { outcome: "error", message: "MedoPilot could not prepare this file right now. Please try again." };
      } finally {
        preparingShareIdsRef.current.delete(documentId);
        setShareProgress(null);
      }
    },
    [client, downloadDocument, fetchOriginalBlob],
  );

  // Called directly from the "Share file" click with no awaited work beforehand, so the
  // browser still sees this as handling that user gesture.
  const commitShare = useCallback(async (file: File): Promise<CommitShareResult> => {
    try {
      await navigator.share({ files: [file] });
      return { outcome: "shared" };
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === "AbortError") {
        return { outcome: "cancelled" };
      }

      // The device supports file sharing but this attempt was blocked (e.g. the OS or
      // browser rejected it). Keep the existing Download action available rather than
      // forcing an unsolicited download/email fallback.
      return { outcome: "blocked", message: "Sharing was blocked by your browser or device. You can still download the file from the file actions." };
    }
  }, []);

  const renameDocument = useCallback(
    async (documentId: string, filename: string) => {
      if (!client) {
        setError("My Files are not available yet.");
        return false;
      }

      try {
        setError(null);
        await client.renameDocument(documentId, filename);
        await refreshDocuments();
        return true;
      } catch (nextError) {
        const message = nextError instanceof Error ? nextError.message : "MedoPilot could not rename this file.";
        return message;
      }
    },
    [client, refreshDocuments],
  );

  const createFolder = useCallback(
    async (name: string, parentFolderId?: string | null) => {
      if (!client) {
        setFolderError("Folders are not available yet.");
        return false;
      }

      try {
        setFolderError(null);
        const folder = await client.createFolder(name, parentFolderId);
        await refreshFolders();
        setFolderFilter(folder.folder_id);
        return folder;
      } catch (nextError) {
        return nextError instanceof Error ? nextError.message : "MedoPilot could not create this folder.";
      }
    },
    [client, refreshFolders],
  );

  const renameFolder = useCallback(
    async (folderId: string, name: string) => {
      if (!client) {
        setFolderError("Folders are not available yet.");
        return false;
      }

      try {
        setFolderError(null);
        await client.renameFolder(folderId, name);
        await refreshFolders();
        return true;
      } catch (nextError) {
        return nextError instanceof Error ? nextError.message : "MedoPilot could not rename this folder.";
      }
    },
    [client, refreshFolders],
  );

  const deleteFolder = useCallback(
    async (folderId: string) => {
      if (!client) {
        setFolderError("Folders are not available yet.");
        return false;
      }

      try {
        setFolderError(null);
        await client.deleteFolder(folderId);
        await refreshFolders();
        if (folderFilter === folderId) {
          setFolderFilter("root");
        }
        return true;
      } catch (nextError) {
        return nextError instanceof Error ? nextError.message : "MedoPilot could not delete this folder.";
      }
    },
    [client, folderFilter, refreshFolders],
  );

  const deleteFolderOnly = useCallback(
    async (folderId: string) => {
      if (!client) {
        setFolderError("Folders are not available yet.");
        return false;
      }

      try {
        setFolderError(null);
        await client.deleteFolder(folderId);
        return true;
      } catch (nextError) {
        return nextError instanceof Error ? nextError.message : "MedoPilot could not delete this folder.";
      }
    },
    [client],
  );

  const moveFolderOnly = useCallback(
    async (folderId: string, parentFolderId: string | null) => {
      if (!client) {
        setFolderError("Folders are not available yet.");
        return false;
      }

      try {
        setFolderError(null);
        await client.moveFolder(folderId, parentFolderId);
        return true;
      } catch (nextError) {
        return nextError instanceof Error ? nextError.message : "MedoPilot could not move this folder.";
      }
    },
    [client],
  );

  const moveDocument = useCallback(
    async (documentId: string, folderId: string | null) => {
      if (!client) {
        setError("My Files are not available yet.");
        return false;
      }

      try {
        setError(null);
        await client.moveDocument(documentId, folderId);
        await refreshDocuments();
        return true;
      } catch (nextError) {
        return nextError instanceof Error ? nextError.message : "MedoPilot could not move this file.";
      }
    },
    [client, refreshDocuments],
  );

  const moveDocumentOnly = useCallback(
    async (documentId: string, folderId: string | null) => {
      if (!client) {
        setError("My Files are not available yet.");
        return false;
      }

      try {
        setError(null);
        await client.moveDocument(documentId, folderId);
        return true;
      } catch (nextError) {
        return nextError instanceof Error ? nextError.message : "MedoPilot could not move this file.";
      }
    },
    [client],
  );

  const deleteDocument = useCallback(
    async (documentId: string) => {
      if (!client) {
        setError("My Files are not available yet.");
        return false;
      }

      try {
        setError(null);
        await client.deleteDocument(documentId);
        await refreshDocuments();
        await refreshTrash();
        return true;
      } catch (nextError) {
        const message = nextError instanceof Error ? nextError.message : "MedoPilot could not remove this file.";
        return message;
      }
    },
    [client, refreshDocuments, refreshTrash],
  );

  const deleteDocumentOnly = useCallback(
    async (documentId: string) => {
      if (!client) {
        setError("My Files are not available yet.");
        return false;
      }

      try {
        setError(null);
        await client.deleteDocument(documentId);
        return true;
      } catch (nextError) {
        const message = nextError instanceof Error ? nextError.message : "MedoPilot could not remove this file.";
        return message;
      }
    },
    [client],
  );

  const restoreDocument = useCallback(
    async (documentId: string) => {
      if (!client) {
        setTrashError("Trash is not available yet.");
        return false;
      }

      try {
        setTrashError(null);
        await client.restoreDocument(documentId);
        await refreshTrash();
        await refreshDocuments();
        return true;
      } catch (nextError) {
        const message = nextError instanceof Error ? nextError.message : "MedoPilot could not restore this file.";
        return message;
      }
    },
    [client, refreshDocuments, refreshTrash],
  );

  const emptyTrash = useCallback(async () => {
    if (!client) {
      return "Trash is not available yet.";
    }
    if (emptyingTrashRef.current) {
      return false;
    }

    emptyingTrashRef.current = true;
    try {
      const result = await client.emptyTrash();
      await refreshTrash();
      return result;
    } catch (nextError) {
      return nextError instanceof Error ? nextError.message : "MedoPilot could not empty Trash.";
    } finally {
      emptyingTrashRef.current = false;
    }
  }, [client, refreshTrash]);

  useEffect(() => {
    void refreshFolders();
  }, [refreshFolders]);

  useEffect(() => {
    void refreshDocuments();
  }, [refreshDocuments]);

  useEffect(() => {
    void refreshTrash();
  }, [refreshTrash]);

  return {
    commitShare,
    createFolder,
    deleteFolder,
    deleteFolderOnly,
    deleteDocument,
    deleteDocumentOnly,
    documents,
    downloadDocument,
    emptyTrash,
    error,
    findDocumentById,
    folderError,
    folderFilter,
    folders,
    folderStatus,
    getDocumentUrl,
    moveDocument,
    moveDocumentOnly,
    moveFolderOnly,
    openDocument,
    prepareShare,
    refreshDocuments,
    refreshFolders,
    refreshTrash,
    renameDocument,
    renameFolder,
    restoreDocument,
    setFolderFilter,
    shareProgress,
    status,
    trashError,
    trashStatus,
    trashedDocuments,
  };
}
